import { Injectable } from '@angular/core';
import { Restangular } from 'ngx-restangular';

export type ScheduledReportStatus = 'draft' | 'active' | 'paused' | 'error';
export type ScheduledReportType = 'template' | 'flat_file';
export type ScheduledReportOutputFormat = 'csv' | 'xlsx';
export type ScheduledReportDeliveryChannel = 'ftp' | 'email';
export type ScheduledReportScheduleType = 'daily' | 'weekly' | 'monthly';
export type ScheduledReportWindowMode =
  | 'current_day'
  | 'previous_day'
  | 'current_week'
  | 'previous_week'
  | 'current_month'
  | 'previous_month'
  | 'rolling_days';
export type ScheduledReportAsOfMode = 'run_time' | 'window_end' | 'manual';
export type ScheduledReportSubscriptionCoverageMode = 'window_overlap' | 'active_as_of';
export type ScheduledReportFilterValueMode = 'saved' | 'runtime';
export type ScheduledReportSensitiveDisplayMode = 'masked' | 'redacted' | 'revealed';
export type ScheduledReportSftpSecretType = 'password' | 'private_key' | 'pgp_public_key';

export interface ScheduledReportSftpSecretReference {
  name: string;
  reference: string;
}

export interface ScheduledReportSftpSecretCatalog {
  passwords: ScheduledReportSftpSecretReference[];
  privateKeys: ScheduledReportSftpSecretReference[];
  pgpPublicKeys: ScheduledReportSftpSecretReference[];
}

export interface ScheduledReportFieldArgSchema {
  type: string;
  required?: boolean;
  ui?: string;
  default?: any;
  label?: string;
}

export interface ScheduledReportFieldDefinition {
  key: string;
  label: string;
  group: string;
  description: string;
  type: string;
  filterOperators: string[];
  sortable: boolean;
  supportedBaseEntities?: string[];
  requiresArgs?: boolean;
  argsSchema?: Record<string, ScheduledReportFieldArgSchema>;
  dataClassification?: string | string[];
  defaultTransform?: ScheduledReportOutputTransform | null;
}

export interface ScheduledReportBaseEntity {
  key: string;
  label: string;
  description: string;
}

export interface ScheduledReportTemplateArgSchema {
  type: string;
  required?: boolean;
  default?: any;
}

export interface ScheduledReportTemplateColumn {
  field: string;
  header: string;
  defaultOrder?: number;
}

export interface ScheduledReportTemplateColumnOverride {
  field: string;
  header?: string;
  reportTotal?: boolean;
}

export interface ScheduledReportTemplateSort {
  field: string;
  direction: 'asc' | 'desc';
  defaultOrder?: number;
}

export interface ScheduledReportTemplate {
  key: string;
  label: string;
  description: string;
  baseEntity: string;
  reportType: 'template';
  supportedOutputFormats: ScheduledReportOutputFormat[];
  supportedDeliveryChannels: ScheduledReportDeliveryChannel[];
  defaultOutputFormat: ScheduledReportOutputFormat;
  defaultFileNamePattern: string;
  supportsColumnCustomization?: boolean;
  supportsSortCustomization?: boolean;
  defaultColumns?: ScheduledReportTemplateColumn[];
  defaultFilters?: ScheduledReportDefinitionFilter[];
  defaultSort?: ScheduledReportTemplateSort[];
  templateArgsSchema?: Record<string, ScheduledReportTemplateArgSchema>;
}

export interface ScheduledReportFieldCatalogResponse {
  baseEntities: ScheduledReportBaseEntity[];
  fields: ScheduledReportFieldDefinition[];
  sensitivePreviewBusinessPurposes?: ScheduledReportBusinessPurpose[];
  scopeOptions?: ScheduledReportScopeOptions;
  vendorCatalog?: ScheduledReportVendorCatalogEntry[];
}

export interface ScheduledReportBusinessPurpose {
  code: string;
  label: string;
}

export interface ScheduledReportScopeOption {
  id: string;
  label: string;
  shortLabel?: string | null;
}

export interface ScheduledReportScopeOptions {
  partner?: ScheduledReportScopeOption[];
  team?: ScheduledReportScopeOption[];
  company?: ScheduledReportScopeOption[];
}

export interface ScheduledReportVendorCatalogEntry {
  vendorSlug: string;
  vendorLabel: string;
  products: Array<{
    productSlug: string;
    label: string;
  }>;
}

export interface ScheduledReportValueOption {
  value: string;
  label: string;
}

const SCHEDULED_REPORT_VENDOR_SLUG_FIELDS = [
  'usersVendorProducts.vendorSlug',
  'vendorSubscriptionCoverage.vendorSlug',
];

const SCHEDULED_REPORT_PRODUCT_SLUG_FIELDS = [
  'usersVendorProducts.productSlug',
  'vendorSubscriptionCoverage.productSlug',
];

const SCHEDULED_REPORT_PARTNER_NAME_FIELDS = [
  'productPaymentTransactions.partnerName',
  'partners.name',
  'teamusers.partnerName',
  'usersVendorProducts.partnerName',
  'groupEnrollments.partnerName',
  'vendorSubscriptionCoverage.partnerName',
];

const SCHEDULED_REPORT_PRODUCT_NAME_FIELDS = [
  'productPaymentTransactions.productName',
  'usersVendorProducts.productName',
  'groupEnrollments.productName',
  'vendorSubscriptionCoverage.productName',
];

export function buildScheduledReportValueOptions(
  catalog: Pick<ScheduledReportFieldCatalogResponse, 'scopeOptions' | 'vendorCatalog'>,
): Record<string, ScheduledReportValueOption[]> {
  const vendorCatalog = catalog.vendorCatalog || [];
  const vendorSlugOptions = uniqueScheduledReportValueOptions(vendorCatalog.map((vendor) => ({
    value: vendor.vendorSlug,
    label: vendor.vendorLabel || vendor.vendorSlug,
  })));
  const productSlugOptions = uniqueScheduledReportValueOptions(vendorCatalog.reduce(
    (options: ScheduledReportValueOption[], vendor) => options.concat((vendor.products || []).map((product) => ({
      value: product.productSlug,
      label: product.label || product.productSlug,
    }))),
    [],
  ));
  const partnerNameOptions = uniqueScheduledReportValueOptions((catalog.scopeOptions?.partner || []).map((partner) => ({
    value: partner.label,
    label: partner.label,
  })));
  const productNameOptions = uniqueScheduledReportValueOptions(vendorCatalog.reduce(
    (options: ScheduledReportValueOption[], vendor) => options.concat((vendor.products || []).map((product) => ({
      value: product.label,
      label: `${vendor.vendorLabel} - ${product.label}`,
    }))),
    [],
  ));

  const valueOptions: Record<string, ScheduledReportValueOption[]> = {};
  assignScheduledReportValueOptions(valueOptions, SCHEDULED_REPORT_VENDOR_SLUG_FIELDS, vendorSlugOptions);
  assignScheduledReportValueOptions(valueOptions, SCHEDULED_REPORT_PRODUCT_SLUG_FIELDS, productSlugOptions);
  assignScheduledReportValueOptions(valueOptions, SCHEDULED_REPORT_PARTNER_NAME_FIELDS, partnerNameOptions);
  assignScheduledReportValueOptions(valueOptions, SCHEDULED_REPORT_PRODUCT_NAME_FIELDS, productNameOptions);
  return valueOptions;
}

function assignScheduledReportValueOptions(
  target: Record<string, ScheduledReportValueOption[]>,
  fieldKeys: string[],
  options: ScheduledReportValueOption[],
): void {
  fieldKeys.forEach((fieldKey) => {
    target[fieldKey] = options;
  });
}

function uniqueScheduledReportValueOptions(
  options: ScheduledReportValueOption[],
): ScheduledReportValueOption[] {
  const optionsByValue = new Map<string, ScheduledReportValueOption>();
  options.forEach((option) => {
    const value = `${option.value || ''}`.trim();
    const label = `${option.label || ''}`.trim();
    if (!value || !label || optionsByValue.has(value)) {
      return;
    }
    optionsByValue.set(value, { value, label });
  });
  return Array.from(optionsByValue.values())
    .sort((left, right) => left.label.localeCompare(right.label) || left.value.localeCompare(right.value));
}

export interface ScheduledReportTemplatesResponse {
  templates: ScheduledReportTemplate[];
}

export interface ScheduledReportDefinitionColumn {
  field?: string;
  header?: string;
  reportTotal?: boolean;
  args?: Record<string, any>;
  dateFormatMode?: 'date' | 'datetime' | 'datetime_timezone' | 'custom';
  dateFormat?: string;
  template?: string;
  sources?: Record<string, {
    field: string;
    args?: Record<string, any>;
    transform?: ScheduledReportOutputTransform | null;
  }>;
  value?: any;
  transform?: ScheduledReportOutputTransform | null;
}

export interface ScheduledReportDefinitionFilter {
  field: string;
  operator: string;
  value?: any;
  args?: Record<string, any>;
  valueMode?: ScheduledReportFilterValueMode;
  parameterKey?: string;
}

export interface ScheduledReportDefinitionSort {
  field: string;
  direction: 'asc' | 'desc';
  args?: Record<string, any>;
}

export interface ScheduledReportDefinitionAggregationLevel {
  field: string;
  header?: string;
  args?: Record<string, any>;
  dateFormatMode?: 'date' | 'datetime' | 'datetime_timezone' | 'custom';
  dateFormat?: string;
}

export interface ScheduledReportDefinitionAggregation {
  type: 'count';
  groupBy: ScheduledReportDefinitionAggregationLevel[];
  countDistinct?: {
    field: string;
    args?: Record<string, any>;
  } | null;
  countHeader?: string;
  reportTotal?: boolean;
}

export interface ScheduledReportDefinitionJson {
  version?: number;
  baseEntity: string;
  temporal?: {
    asOfMode?: ScheduledReportAsOfMode;
    subscriptionCoverageMode?: ScheduledReportSubscriptionCoverageMode;
  };
  audience?: {
    includeUsersWithoutCurrentTeamMembership?: boolean;
  };
  filters?: ScheduledReportDefinitionFilter[];
  columns: ScheduledReportDefinitionColumn[];
  sort?: ScheduledReportDefinitionSort[];
  distinct?: boolean;
  aggregation?: ScheduledReportDefinitionAggregation | null;
}

export interface ScheduledReportScheduleConfig {
  time: string;
  windowMode: ScheduledReportWindowMode;
  daysOfWeek?: number[];
  dayOfMonth?: number;
  rollingDays?: number;
}

export interface ScheduledReportRuntimeConfig {
  skipIfEmpty?: boolean;
  storeArtifact?: boolean;
  artifactRetentionDays?: number;
  showReportDates?: boolean;
}

export interface ScheduledReportFtpDeliveryConfig {
  host?: string;
  port?: number;
  username?: string;
  passwordSecretKey?: string;
  privateKeySecretKey?: string;
  folder?: string;
  fileNamePrefix?: string;
  pgp?: {
    enabled: boolean;
    publicKeySecretKey?: string | null;
  };
}

export interface ScheduledReportEmailDeliveryConfig {
  to: string[];
  cc?: string[];
  bcc?: string[];
  subjectTemplate?: string;
  bodyTemplate?: string;
  attachFile?: boolean;
}

export type ScheduledReportDeliveryConfig = Record<string, any>;

export interface ScheduledReport {
  id: string;
  name: string;
  description?: string | null;
  status: ScheduledReportStatus | string;
  scopeType: 'partner' | 'team' | 'company' | 'global' | string;
  scopeId?: string | null;
  scopeIds?: string[] | null;
  reportType: ScheduledReportType | string;
  templateKey?: string | null;
  templateArgs?: Record<string, any> | null;
  baseEntity?: string | null;
  scheduleType: ScheduledReportScheduleType | string;
  scheduleConfig: ScheduledReportScheduleConfig;
  timezone: string;
  nextRunAt?: string | null;
  lastRunAt?: string | null;
  outputFormat: ScheduledReportOutputFormat | string;
  fileNamePattern?: string | null;
  definitionJson: ScheduledReportDefinitionJson;
  deliveryChannel: ScheduledReportDeliveryChannel | string;
  deliveryConfig: ScheduledReportDeliveryConfig;
  runtimeConfig?: ScheduledReportRuntimeConfig | null;
  lastSuccessRunId?: string | null;
  lastErrorMessage?: string | null;
  createdByUserId?: string | null;
  updatedByUserId?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface ScheduledReportPreview {
  columns: Array<{ header: string; field: string | null }>;
  sampleRows: Array<Record<string, any>>;
  totals?: Record<string, any> | null;
  totalRow?: Record<string, any> | null;
  totalCount: number;
  sensitiveDisplayMode?: ScheduledReportSensitiveDisplayMode;
  warnings: string[];
  runContext: {
    timezone: string;
    windowStart: string;
    windowEnd: string;
    asOfAt: string;
  };
}

export interface ScheduledReportRun {
  id: string;
  scheduledReportId: string;
  status: 'queued' | 'running' | 'success' | 'failed' | 'skipped' | string;
  triggerType: 'schedule' | 'manual_retry' | 'manual_run' | string;
  startedAt?: string | null;
  finishedAt?: string | null;
  windowStart?: string | null;
  windowEnd?: string | null;
  asOfAt?: string | null;
  rowsGenerated?: number | null;
  outputFormat?: string | null;
  fileName?: string | null;
  artifactUrl?: string | null;
  compiledQuerySummary?: Record<string, any> | null;
  definitionSnapshot?: Record<string, any> | null;
  deliveryChannel?: string | null;
  deliveryResult?: Record<string, any> | null;
  errorMessage?: string | null;
  createdAt?: string | null;
}

export interface ScheduledReportRunResult {
  runId: string;
  status: string;
  rowsGenerated?: number;
  fileName?: string;
  artifactUrl?: string | null;
  deliveryResult?: Record<string, any>;
  runContext?: {
    timezone: string;
    windowStart: string;
    windowEnd: string;
    asOfAt: string;
  };
  warnings?: string[];
}

export interface ScheduledReportRunDeliveryOverride {
  deliveryChannel: Extract<ScheduledReportDeliveryChannel, 'email'>;
  deliveryConfig: ScheduledReportEmailDeliveryConfig;
}

export interface ScheduledReportPreviewOverrides {
  includeDemoUsers?: boolean;
  windowStart?: string;
  windowEnd?: string;
  asOfAt?: string;
  referenceNow?: string;
  rollingDays?: number;
  runtimeParameters?: Record<string, any>;
}

export interface ScheduledReportSensitiveAccess {
  businessPurposeCode: string;
  referenceType: string;
  referenceId: string;
}

export interface ScheduledReportRunOptions {
  deliveryOverride?: ScheduledReportRunDeliveryOverride;
  runtimeParameters?: Record<string, any>;
  sensitiveAccess?: ScheduledReportSensitiveAccess;
}

export type ScheduledReportOutputTransform = 'currency' | 'decrypt' | 'decrypt_digits' | 'yes_no';

export type ScheduledReportPayload = Partial<ScheduledReport> & {
  name: string;
  status: ScheduledReportStatus | string;
  scopeType: string;
  scopeId?: string | null;
  scopeIds?: string[] | null;
  reportType: ScheduledReportType | string;
  outputFormat: ScheduledReportOutputFormat | string;
  scheduleType: ScheduledReportScheduleType | string;
  scheduleConfig: ScheduledReportScheduleConfig;
  timezone: string;
  deliveryChannel: ScheduledReportDeliveryChannel | string;
  deliveryConfig: ScheduledReportDeliveryConfig;
};

@Injectable({
  providedIn: 'root'
})
export class ScheduledReportsService {
  private readonly basePath = 'admin/reports/scheduled';

  constructor(private restangular: Restangular) {}

  listReports(params?: { status?: string | null; limit?: number; offset?: number }): Promise<ScheduledReport[]> {
    return this.restangular.one(this.basePath).customGET('', params || {}).toPromise();
  }

  getReport(id: string): Promise<ScheduledReport> {
    return this.restangular.one(this.basePath, id).customGET().toPromise();
  }

  createReport(payload: ScheduledReportPayload): Promise<ScheduledReport> {
    return this.restangular.one(this.basePath).customPOST(payload).toPromise();
  }

  updateReport(id: string, payload: Partial<ScheduledReportPayload>): Promise<ScheduledReport> {
    return this.restangular.one(this.basePath, id).customPATCH(payload).toPromise();
  }

  deleteReport(id: string): Promise<void> {
    return this.restangular.one(this.basePath, id).customDELETE().toPromise();
  }

  previewDraft(
    payload: ScheduledReportPayload,
    limit = 25,
    runtimeParameters?: Record<string, any>,
    includeDemoUsers = false,
  ): Promise<ScheduledReportPreview> {
    const previewPayload = {
      ...payload,
      ...(runtimeParameters ? { runtimeParameters } : {}),
      ...(includeDemoUsers ? { includeDemoUsers } : {}),
    };
    return this.restangular.one(this.basePath).one('preview').customPOST(previewPayload, '', { limit }).toPromise();
  }

  previewSaved(id: string, overrides?: ScheduledReportPreviewOverrides, limit = 25): Promise<ScheduledReportPreview> {
    return this.restangular
      .one(this.basePath, id)
      .one('preview')
      .customPOST(overrides || {}, '', { limit })
      .toPromise();
  }

  previewSensitiveDraft(
    payload: ScheduledReportPayload,
    sensitiveAccess: ScheduledReportSensitiveAccess | null,
    sensitiveDisplayMode: ScheduledReportSensitiveDisplayMode = 'masked',
    limit = 25,
    runtimeParameters?: Record<string, any>,
    includeDemoUsers = false,
  ): Promise<ScheduledReportPreview> {
    return this.restangular
      .one(this.basePath)
      .one('preview-sensitive')
      .customPOST({
        ...payload,
        ...(includeDemoUsers ? { includeDemoUsers } : {}),
        ...(runtimeParameters ? { runtimeParameters } : {}),
        ...(sensitiveAccess ? { sensitiveAccess } : {}),
        sensitiveDisplayMode,
      }, '', { limit })
      .toPromise();
  }

  previewSensitiveSaved(
    id: string,
    sensitiveAccess: ScheduledReportSensitiveAccess | null,
    sensitiveDisplayMode: ScheduledReportSensitiveDisplayMode = 'masked',
    overrides?: ScheduledReportPreviewOverrides,
    limit = 25,
  ): Promise<ScheduledReportPreview> {
    return this.restangular
      .one(this.basePath, id)
      .one('preview-sensitive')
      .customPOST({
        ...(overrides || {}),
        ...(sensitiveAccess ? { sensitiveAccess } : {}),
        sensitiveDisplayMode,
      }, '', { limit })
      .toPromise();
  }

  runNow(
    id: string,
    runtimeParameters?: Record<string, any>,
    sensitiveAccess?: ScheduledReportSensitiveAccess,
  ): Promise<ScheduledReportRunResult> {
    const payload: ScheduledReportRunOptions = {
      ...(runtimeParameters ? { runtimeParameters } : {}),
      ...(sensitiveAccess ? { sensitiveAccess } : {}),
    };
    return this.restangular.one(this.basePath, id).one('run').customPOST(payload).toPromise();
  }

  runNowWithDeliveryOverride(id: string, deliveryOverride: ScheduledReportRunDeliveryOverride, runtimeParameters?: Record<string, any>): Promise<ScheduledReportRunResult> {
    const payload: ScheduledReportRunOptions = runtimeParameters
      ? { deliveryOverride, runtimeParameters }
      : { deliveryOverride };
    return this.restangular.one(this.basePath, id).one('run').customPOST(payload).toPromise();
  }

  rerun(
    id: string,
    overrides: ScheduledReportPreviewOverrides & {
      reason?: string;
      deliveryOverride?: ScheduledReportRunDeliveryOverride;
    },
  ): Promise<ScheduledReportRunResult> {
    return this.restangular.one(this.basePath, id).one('rerun').customPOST(overrides || {}).toPromise();
  }

  listRuns(id: string, params?: { limit?: number; offset?: number }): Promise<ScheduledReportRun[]> {
    return this.restangular.one(this.basePath, id).one('runs').customGET('', params || {}).toPromise();
  }

  getFieldCatalog(): Promise<ScheduledReportFieldCatalogResponse> {
    return this.restangular.one(this.basePath).one('field-catalog').customGET().toPromise();
  }

  getTemplates(): Promise<ScheduledReportTemplatesResponse> {
    return this.restangular.one(this.basePath).one('templates').customGET().toPromise();
  }

  getSftpSecretCatalog(): Promise<ScheduledReportSftpSecretCatalog> {
    return this.restangular.one(this.basePath).one('sftp-secrets').customGET().toPromise();
  }

  createSftpSecret(
    type: ScheduledReportSftpSecretType,
    name: string,
    value: string,
  ): Promise<ScheduledReportSftpSecretReference & { type: ScheduledReportSftpSecretType }> {
    return this.restangular.one(this.basePath).one('sftp-secrets').customPOST({ type, name, secretValue: value }).toPromise();
  }
}
